/**
 * Project a recipient's REAL slotting appointments into DataTable row records
 * for the send / test-mail / builder HTML preview — the client-side twin of the
 * consumer's `context-enricher.enrichAppointments` (main-consumer). At real send
 * the consumer joins the recipient's appointments to offering/treatment/lane and
 * emits one row per appointment keyed by the catalog `key`, PRE-formatting dates
 * in the event timezone + recipient language; the marker expander then renders
 * (and status-filters / sorts / humanizes) them.
 *
 * The preview has no payload, so we fetch the appointments client-side
 * (`getGuestAppointmentsPreview`) and run the SAME projection here so the admin
 * previews the actual rows the recipient will receive instead of illustrative
 * SAMPLE rows. `appointmentStatus` stays RAW here (the expander needs the raw
 * enum to apply the per-marker `cfg.filter.statuses`, then humanizes — exactly
 * like the consumer renderer).
 *
 * Date/time/duration/status formatting is PORTED verbatim from the consumer's
 * `humanize.ts` (JS → JS) so the preview's `Intl` output matches the inbox to
 * the character — NOT reusing the FE `datetime-tz.ts` formatters, which use a
 * different style (weekday + short month) than the consumer's `dateStyle:'long'`.
 *
 * KEEP IN SYNC with:
 *  - main-consumer/src/lib/context-enricher.ts (enrichAppointments — projection)
 *  - main-consumer/src/lib/humanize.ts (formatDate/Time/durationMinutes +
 *    humanizeAppointmentStatus + the label table)
 *  - main-consumer/src/lib/data-table-render.ts (DEFAULT_INCLUDED_STATUSES)
 */
import { APPOINTMENT_COLUMNS } from "./data-table-catalog";

/**
 * Raw appointment row from the backend preview endpoint
 * (`GET /api/v1/slotting/{eventId}/guest/{guestId}/appointments-preview`,
 * serialised from `GuestAppointmentPreviewRow`). Deliberately raw: ISO instants,
 * the status enum, minutes — all formatted client-side below.
 */
export interface AppointmentPreviewRow {
  offeringName?: string | null;
  offeringDescription?: string | null;
  treatmentName?: string | null;
  treatmentDurationMinutes?: number | null;
  treatmentImageUrl?: string | null;
  laneName?: string | null;
  laneColor?: string | null;
  startDt?: string | null;
  endDt?: string | null;
  status?: string | null;
  notes?: string | null;
  cancelledReason?: string | null;
}

// ── Locale / time formatting (ported from main-consumer humanize.ts) ──

function localeFor(lang: string | null | undefined): string {
  if (typeof lang !== "string" || lang.length === 0) return "en";
  return lang;
}

/** Lower-cased base language subtag ("de-DE" → "de", "" → ""). */
function baseLang(lang: string | null | undefined): string {
  if (typeof lang !== "string") return "";
  return lang.trim().toLowerCase().split("-")[0] ?? "";
}

/** Primary language subtag (e.g. "de-CH" → "de"). Defaults to "en". */
function primarySubtag(lang: string | null | undefined): string {
  return typeof lang === "string" && lang.length > 0
    ? lang.split("-")[0]!.toLowerCase()
    : "en";
}

// "o'clock" word appended in West-Germanic locales (Intl never emits it).
const CLOCK_WORD: Record<string, string> = { de: "Uhr", nl: "uur", lb: "Auer" };

// Locales whose official clock replaces the colon with an "h".
const HOUR_SEPARATOR: Record<string, string> = { fr: " h ", pt: "h" };

// Force 24h for every supported language except English.
function force24h(lang: string | null | undefined): boolean {
  return baseLang(lang) !== "en";
}

function coerceDate(input: string | null | undefined): Date | null {
  if (input == null) return null;
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

/** Long-form localized date in the event timezone ("19. Mai 2026"). */
export function formatApptDate(
  input: string | null | undefined,
  lang: string | null | undefined,
  timeZone: string | null | undefined,
): string {
  const d = coerceDate(input);
  if (d === null) return "";
  try {
    return new Intl.DateTimeFormat(localeFor(lang), {
      dateStyle: "long",
      ...(timeZone ? { timeZone } : {}),
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/** Short localized time in the event timezone ("14:30 Uhr" / "2:30 PM"). */
export function formatApptTime(
  input: string | null | undefined,
  lang: string | null | undefined,
  timeZone: string | null | undefined,
): string {
  const d = coerceDate(input);
  if (d === null) return "";
  try {
    const sep = HOUR_SEPARATOR[baseLang(lang)];
    if (sep !== undefined) {
      const parts = new Intl.DateTimeFormat(localeFor(lang), {
        hour: "numeric",
        minute: "2-digit",
        hourCycle: "h23",
        ...(timeZone ? { timeZone } : {}),
      }).formatToParts(d);
      const hour = parts.find((p) => p.type === "hour")?.value ?? "";
      const minute = parts.find((p) => p.type === "minute")?.value ?? "";
      return `${hour}${sep}${minute}`;
    }
    const time = new Intl.DateTimeFormat(localeFor(lang), {
      timeStyle: "short",
      ...(force24h(lang) ? { hourCycle: "h23" as const } : {}),
      ...(timeZone ? { timeZone } : {}),
    }).format(d);
    const word = CLOCK_WORD[baseLang(lang)];
    return word ? `${time} ${word}` : time;
  } catch {
    return d.toISOString().slice(11, 16);
  }
}

/** Whole minutes between two ISO instants, or null. */
function durationMinutes(
  start: string | null | undefined,
  end: string | null | undefined,
): number | null {
  const s = coerceDate(start);
  const e = coerceDate(end);
  if (s === null || e === null) return null;
  return Math.max(0, Math.round((e.getTime() - s.getTime()) / 60000));
}

// ── Appointment status (ported from main-consumer humanize.ts) ──

/**
 * Raw AppointmentStatus values included by default when a data table doesn't
 * configure its own `filter.statuses` (CANCELLED excluded). KEEP IN SYNC with
 * main-consumer/src/lib/data-table-render.ts DEFAULT_INCLUDED_STATUSES.
 */
export const DEFAULT_INCLUDED_STATUSES: ReadonlySet<string> = new Set([
  "TENTATIVE",
  "CONFIRMED",
  "CHECKED_IN",
]);

const APPOINTMENT_STATUS_ORDER = [
  "TENTATIVE",
  "CONFIRMED",
  "CHECKED_IN",
  "CANCELLED",
] as const;

/** Localized AppointmentStatus labels, ordered to APPOINTMENT_STATUS_ORDER.
 *  KEEP IN SYNC with main-consumer/src/lib/humanize.ts APPOINTMENT_STATUS_LABELS. */
const APPOINTMENT_STATUS_LABELS: Record<string, readonly string[]> = {
  bg: ["Условно", "Потвърдено", "Регистриран", "Отменено"],
  ca: ["Provisional", "Confirmat", "Registrat", "Cancel·lat"],
  cs: ["Předběžné", "Potvrzeno", "Přihlášeno", "Zrušeno"],
  cy: ["Dros dro", "Wedi cadarnhau", "Wedi cofrestru", "Wedi'i ganslo"],
  da: ["Foreløbig", "Bekræftet", "Tjekket ind", "Annulleret"],
  de: ["Vorläufig", "Bestätigt", "Eingecheckt", "Storniert"],
  el: ["Προσωρινό", "Επιβεβαιωμένο", "Έγινε check-in", "Ακυρώθηκε"],
  en: ["Tentative", "Confirmed", "Checked In", "Cancelled"],
  es: ["Tentativo", "Confirmado", "Registrado", "Cancelado"],
  et: ["Esialgne", "Kinnitatud", "Sisse registreeritud", "Tühistatud"],
  eu: ["Behin-behinekoa", "Berretsita", "Check-In eginda", "Bertan behera utzita"],
  fi: ["Alustava", "Vahvistettu", "Sisäänkirjautunut", "Peruutettu"],
  fr: ["Provisoire", "Confirmé", "Enregistré", "Annulé"],
  ga: ["Sealadach", "Deimhnithe", "Seiceáilte Isteach", "Curtha ar Ceal"],
  gd: ["Mì-chinnteach", "Air a dhearbhadh", "Air clàrachadh a-steach", "Air a sgur dheth"],
  gl: ["Provisional", "Confirmado", "Rexistrado", "Cancelado"],
  hr: ["Privremeno", "Potvrđeno", "Prijavljeno", "Otkazano"],
  hu: ["Előzetes", "Megerősítve", "Becsekkolva", "Lemondva"],
  is: ["Til bráðabirgða", "Staðfest", "Innritað", "Aflýst"],
  it: ["Provvisorio", "Confermato", "Registrato", "Annullato"],
  lb: ["Virleefeg", "Bestätegt", "Acgecheckt", "Annuléiert"],
  lt: ["Preliminari", "Patvirtinta", "Užregistruota", "Atšaukta"],
  lv: ["Provizorisks", "Apstiprināts", "Reģistrējies", "Atcelts"],
  mt: ["Tentattiv", "Ikkonfermat", "Iċċekkjat", "Ikkanċellat"],
  nb: ["Foreløpig", "Bekreftet", "Innsjekket", "Avlyst"],
  nl: ["Voorlopig", "Bevestigd", "Ingecheckt", "Geannuleerd"],
  pl: ["Wstępny", "Potwierdzony", "Odprawiony", "Anulowany"],
  pt: ["Provisório", "Confirmado", "Com Check-In", "Cancelado"],
  rm: ["Provisoric", "Confermà", "Registrà", "Annullà"],
  ro: ["Provizoriu", "Confirmat", "Check-in efectuat", "Anulat"],
  sk: ["Predbežné", "Potvrdené", "Prihlásený", "Zrušené"],
  sl: ["Pogojno", "Potrjeno", "Prijavljen", "Preklicano"],
  sq: ["I përkohshëm", "Konfirmuar", "I regjistruar", "Anuluar"],
  sr: ["Privremeno", "Potvrđeno", "Prijavljeno", "Otkazano"],
  sv: ["Preliminär", "Bekräftad", "Incheckad", "Avbokad"],
};

function titleCaseEnum(s: string): string {
  return s
    .toLowerCase()
    .split(/[_\s]+/)
    .filter((part) => part.length > 0)
    .map((part, idx) => (idx === 0 ? part[0]!.toUpperCase() + part.slice(1) : part))
    .join(" ");
}

/** Localized label for a raw AppointmentStatus enum value. Mirrors the consumer
 *  renderer's post-filter humanization. */
export function humanizeAppointmentStatus(
  value: unknown,
  lang?: string | null,
): string {
  if (typeof value !== "string" || value.length === 0) return "";
  const idx = APPOINTMENT_STATUS_ORDER.indexOf(
    value as (typeof APPOINTMENT_STATUS_ORDER)[number],
  );
  if (idx === -1) return titleCaseEnum(value);
  const row = APPOINTMENT_STATUS_LABELS[primarySubtag(lang)] ?? APPOINTMENT_STATUS_LABELS["en"]!;
  return row[idx] ?? titleCaseEnum(value);
}

// ── Seat / email status (ported from main-backend FieldValueFormatter) ──

const SEAT_STATUS_ORDER = [
  "unassigned", "assigned", "tentative", "reserved", "checkedIn",
] as const;
/** KEEP IN SYNC with main-backend FieldValueFormatter.SEAT_STATUS_LABELS +
 *  main-consumer humanize.SEAT_STATUS_LABELS. Unlisted langs → title-case. */
const SEAT_STATUS_LABELS: Record<string, readonly string[]> = {
  en: ["Unassigned", "Assigned", "Tentative", "Reserved", "Checked In"],
  de: ["Nicht zugewiesen", "Zugewiesen", "Vorläufig", "Reserviert", "Eingecheckt"],
  es: ["No asignado", "Asignado", "Provisional", "Reservado", "Registrado"],
  fr: ["Non attribué", "Attribué", "Provisoire", "Réservé", "Enregistré"],
  it: ["Non assegnato", "Assegnato", "Provvisorio", "Riservato", "Registrato"],
  nl: ["Niet toegewezen", "Toegewezen", "Voorlopig", "Gereserveerd", "Ingecheckt"],
  pt: ["Não atribuído", "Atribuído", "Provisório", "Reservado", "Registado"],
};

/** Localized label for a raw seat-status value. Unknown → title-case. */
export function humanizeSeatStatus(value: unknown, lang?: string | null): string {
  if (typeof value !== "string" || value.length === 0) return "";
  const idx = SEAT_STATUS_ORDER.indexOf(value as (typeof SEAT_STATUS_ORDER)[number]);
  if (idx === -1) return titleCaseEnum(value);
  const row = SEAT_STATUS_LABELS[primarySubtag(lang)] ?? SEAT_STATUS_LABELS["en"]!;
  return row[idx] ?? titleCaseEnum(value);
}

// APPEND-ONLY. The rows below are POSITIONAL — indexOf into ORDER indexes
// straight into row[]. Inserting in the middle shifts every label after it, in
// all seven languages, here and in both siblings.
const EMAIL_STATUS_ORDER = [
  "SUCCESS", "SENT", "DELIVERED", "BOUNCED", "COMPLAINED", "REJECTED",
  "FAILED", "DELAYED", "PENDING", "SKIPPED", "OPEN", "CLICK", "SOFT_BOUNCED",
] as const;
/** KEEP IN SYNC with main-backend FieldValueFormatter.EMAIL_STATUS_LABELS +
 *  main-consumer humanize.EMAIL_STATUS_LABELS. Unlisted langs → title-case. */
const EMAIL_STATUS_LABELS: Record<string, readonly string[]> = {
  en: ["Successful", "Sent", "Delivered", "Bounced", "Complaint", "Rejected", "Failed", "Delayed", "Pending", "Skipped", "Open", "Clicked", "Temporarily deferred"],
  de: ["Erfolgreich", "Gesendet", "Zugestellt", "Zurückgewiesen", "Beschwerde", "Abgelehnt", "Fehlgeschlagen", "Verzögert", "Ausstehend", "Übersprungen", "Offen", "Geklickt", "Vorübergehend abgewiesen"],
  es: ["Exitoso", "Enviado", "Entregado", "Rebotado", "Queja", "Rechazado", "Fallido", "Retrasado", "Pendiente", "Omitido", "Abierto", "Clic", "Aplazado temporalmente"],
  fr: ["Réussi", "Envoyé", "Livré", "Rejeté", "Réclamation", "Refusé", "Échoué", "Retardé", "En attente", "Ignoré", "Ouvert", "Cliqué", "Différé temporairement"],
  it: ["Riuscito", "Inviato", "Recapitato", "Respinto", "Reclamo", "Rifiutato", "Fallito", "Ritardato", "In attesa", "Saltato", "Aperto", "Cliccato", "Differito temporaneamente"],
  nl: ["Succesvol", "Verzonden", "Bezorgd", "Geweigerd", "Klacht", "Afgewezen", "Mislukt", "Vertraagd", "In afwachting", "Overgeslagen", "Open", "Geklikt", "Tijdelijk uitgesteld"],
  pt: ["Bem-sucedido", "Enviado", "Entregue", "Rejeitado", "Reclamação", "Recusado", "Falhado", "Atrasado", "Pendente", "Ignorado", "Aberto", "Clicado", "Adiado temporariamente"],
};

/** Localized label for a raw email-status value. Unknown → title-case. */
export function humanizeEmailStatus(value: unknown, lang?: string | null): string {
  if (typeof value !== "string" || value.length === 0) return "";
  const idx = EMAIL_STATUS_ORDER.indexOf(value as (typeof EMAIL_STATUS_ORDER)[number]);
  if (idx === -1) return titleCaseEnum(value);
  const row = EMAIL_STATUS_LABELS[primarySubtag(lang)] ?? EMAIL_STATUS_LABELS["en"]!;
  return row[idx] ?? titleCaseEnum(value);
}

/**
 * Project the recipient's real appointments into preview row records keyed by
 * the catalog `key`. `lang` is the render/recipient language and `tz` the event
 * timezone — both drive date/time formatting (must match the inbox). Status is
 * left RAW; the marker expander filters then humanizes it.
 */
export function buildAppointmentRecords(
  rows: AppointmentPreviewRow[],
  lang: string | null | undefined,
  tz: string | null | undefined,
): Record<string, string>[] {
  return rows.map((a) => {
    const rec: Record<string, string> = {};
    for (const col of APPOINTMENT_COLUMNS) rec[col.key] = "";
    rec["offeringName"] = a.offeringName ?? "";
    rec["offeringDescription"] = a.offeringDescription ?? "";
    rec["treatmentName"] = a.treatmentName ?? "";
    rec["treatmentDuration"] =
      a.treatmentDurationMinutes != null ? String(a.treatmentDurationMinutes) : "";
    rec["treatmentImage"] = a.treatmentImageUrl ?? "";
    rec["laneName"] = a.laneName ?? "";
    rec["laneColor"] = a.laneColor ?? "";
    rec["appointmentDate"] = formatApptDate(a.startDt, lang, tz);
    rec["appointmentTime"] = formatApptTime(a.startDt, lang, tz);
    rec["appointmentEndTime"] = formatApptTime(a.endDt, lang, tz);
    const mins = durationMinutes(a.startDt, a.endDt);
    rec["appointmentDuration"] = mins != null ? String(mins) : "";
    // RAW enum — the expander applies cfg.filter.statuses then humanizes.
    rec["appointmentStatus"] = a.status ?? "";
    rec["appointmentNotes"] = a.notes ?? "";
    rec["cancelledReason"] = a.cancelledReason ?? "";
    return rec;
  });
}
